export interface MappedFeatureError {
  fieldErrors: Record<string, string>;
  message: string;
}

/**
 * A 422 `errors[]` item arrives in one of two shapes:
 *  - Permission-map validator: `{ module, action?, message }` — keyed under the
 *    literal form-field name `'module'`.
 *  - Generic Mongoose / global error handler: `{ field, message }` — keyed under
 *    the `field` value (e.g. `'key'`, `'name'`, `'route'`).
 */
interface ValidationItem {
  module?: string;
  action?: string;
  field?: string;
  message?: string;
}

const FALLBACK = 'Failed to save feature';

/**
 * Translate an axios error from the feature API into inline field errors plus a
 * toast message. 409 → duplicate key on the `key` field; 422 → per-field from
 * `data.errors[]`; everything else → toast-only.
 */
export function mapFeatureApiError(err: unknown): MappedFeatureError {
  const e = err as { response?: { status?: number; data?: { message?: string; errors?: ValidationItem[] } } };
  const status = e?.response?.status;
  const data = e?.response?.data;

  if (status === 409) {
    const msg = 'A feature with this key already exists';
    return { fieldErrors: { key: msg }, message: msg };
  }

  if (status === 422 && Array.isArray(data?.errors) && data.errors.length > 0) {
    const fieldErrors: Record<string, string> = {};
    for (const item of data.errors) {
      // Use the property name as the key: explicit `field` wins, then `module`
      const key = item.field != null ? item.field : item.module != null ? 'module' : '';
      if (key && item.message && !fieldErrors[key]) fieldErrors[key] = item.message;
    }
    const summarized = data.errors
      .slice(0, 2)
      .map((it) => {
        const head = it.module && it.action ? `${it.module}.${it.action}` : it.module ?? it.field ?? '';
        return head ? `${head}: ${it.message ?? ''}` : it.message ?? '';
      })
      .filter(Boolean)
      .join(' • ');
    const message = summarized
      ? data.errors.length > 2
        ? `${summarized} (+${data.errors.length - 2} more)`
        : summarized
      : data?.message ?? FALLBACK;
    return { fieldErrors, message };
  }

  return { fieldErrors: {}, message: data?.message ?? FALLBACK };
}
